/**
 * bridge/theme_controller.js — Hotwire Native "theme" bridge component (web side).
 *
 * Identifier: bridge--theme (glob-derived from the bridge/ subdir; wire with
 * data-controller="bridge--theme"). static component = "theme" MUST equal the
 * iOS ThemeComponent `class var name`.
 *
 * Capability tier: syncs native chrome (status bar / bottom nav) to the web
 * palette so the shell uses the active business theme's resolved navigation
 * colors, and lets the web read the device's resolved system appearance back.
 *
 * It extends the Strada BridgeComponent (so it only connect()s inside the iOS
 * Hotwire Native shell and gets `this.send`), and contributes the `theme`
 * implementation triple to the window.ChippieBridge facade:
 *
 *   - ios:     send a "connect" event to ThemeComponent.swift carrying the mode
 *              and resolved navigation colors; the reply { appearance } is
 *              handed to the caller's callback.
 *   - android: call window.ChippieNative.setTheme(mode, background, foreground)
 *              (no Strada on Android).
 *   - pwa:     no-op (the browser owns its own chrome; nothing native to sync).
 *
 * Callers do `window.ChippieBridge.theme(mode, background, foreground, cb)` and
 * the facade picks the right transport. `mode` is "light" | "dark" | "system"
 * (default "system"). Automatic sync reads the colors from the web theme.
 *
 * On load this module pushes the page's CURRENT palette to Android; on iOS the
 * controller does the same when Strada connects. Turbo loads re-sync Android,
 * while iOS reconnects with the replaced body. The mode comes from
 * <html data-theme-scheme>; colors come from the computed
 * --nav-bg-native/--nav-text tokens, keeping the web theme CSS as the palette
 * SSOT.
 *
 * Outside the iOS shell BridgeComponent never connect()s; the Android/PWA impls
 * are registered standalone at module load so theme() still resolves there.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Read the page's active palette. data-theme-scheme is the server's resolved
// light/dark answer for the active theme (ThemeService owns the mapping via each
// theme's category, so named themes resolve correctly, not just one literally
// called "dark"). When it is absent we defer to the OS preference.
function currentMode() {
    const scheme = (document.documentElement.dataset.themeScheme || "").toLowerCase();
    if (scheme === "light" || scheme === "dark") return scheme;
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }
    return "light";
}

function currentTheme() {
    const styles = window.getComputedStyle(document.documentElement);
    // --nav-bg-native is the solid stand-in the native shells paint with; it
    // resolves to --nav-bg on every theme except the ones whose nav is a
    // gradient (rustic). Fall back to --nav-bg if a stale cached stylesheet
    // predates the token.
    const navBackground = styles.getPropertyValue("--nav-bg-native").trim()
        || styles.getPropertyValue("--nav-bg").trim();
    return {
        mode: currentMode(),
        navBackground,
        navForeground: styles.getPropertyValue("--nav-text").trim(),
    };
}

// Android + PWA impls carry no per-instance state, so register them once at
// module load. The iOS impl is wired by the controller's connect() (it needs a
// live Strada `this.send`). The facade is the SSOT for which one runs.
function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("theme", {
        // Overridden by connect() with a real Strada send inside the iOS shell;
        // safe no-op if selected before connect.
        ios: () => {},
        android: (mode, navBackground, navForeground) => {
            if (window.ChippieNative && window.ChippieNative.setTheme) {
                try {
                    window.ChippieNative.setTheme(
                        mode || "system",
                        navBackground || "",
                        navForeground || "",
                    );
                } catch (_e) { /* bridge gone */ }
            }
        },
        // No native chrome to sync on the PWA — the browser owns its own bars.
        pwa: () => {},
    });
}

function syncCurrentTheme() {
    if (!window.ChippieBridge || !window.ChippieBridge.theme) return;
    const theme = currentTheme();
    window.ChippieBridge.theme(
        theme.mode,
        theme.navBackground,
        theme.navForeground,
    );
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
    syncCurrentTheme();
} else {
    document.addEventListener("DOMContentLoaded", () => {
        registerStandalone();
        syncCurrentTheme();
    }, { once: true });
}
document.addEventListener("turbo:load", syncCurrentTheme);

export default class extends BridgeComponent {
    static component = "theme";

    connect() {
        super.connect();
        // Inside the iOS shell now: wire the real Strada send into the facade,
        // keeping the Android/PWA fallbacks intact.
        if (window.ChippieBridge && window.ChippieBridge.register) {
            window.ChippieBridge.register("theme", {
                ios: (mode, navBackground, navForeground, callback) => this.send("connect", {
                    mode: mode || "system",
                    navBackground: navBackground || "",
                    navForeground: navForeground || "",
                }, (message) => {
                    if (callback) callback(message.data ? message.data.appearance : undefined);
                }),
                android: (mode, navBackground, navForeground) => {
                    if (window.ChippieNative && window.ChippieNative.setTheme) {
                        try {
                            window.ChippieNative.setTheme(
                                mode || "system",
                                navBackground || "",
                                navForeground || "",
                            );
                        } catch (_e) { /* bridge gone */ }
                    }
                },
                pwa: () => {},
            });
        }

        // Push the page's current palette to native once so the bars match
        // immediately on first paint inside the shell.
        this.send("connect", currentTheme());
    }

    disconnect() {
        // Leaving the iOS shell context: drop the live-send impl back to the
        // standalone placeholder so a stale `this.send` is never invoked.
        registerStandalone();
        super.disconnect();
    }
}
